import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getActivePrestationBrandConfig } from 'src/config/environment.config';
import useAppBar from 'src/Hooks/Dashboard/useAppBar';
import EuroIcon from '@mui/icons-material/Euro';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShieldIcon from '@mui/icons-material/Shield';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ButtonField from '../../../Components/Ui/ButtonField';
import useTailwindBrand from '../../../Theme/useTailwindBrand';
import sapLogo from '../../../Assets/Images/logo-sap.svg';

const PRIMARY = '#0054A3';
const SECONDARY = '#219cff';

const STEPS = [
    {
        icon: <AssignmentTurnedInIcon sx={{ fontSize: 28, color: PRIMARY }} />,
        num: '01',
        label: 'Remplissez le formulaire',
        detail: "Vos infos personnelles et IBAN. Tout se fait en ligne, en 2 minutes.",
        time: '~2 min',
        color: PRIMARY,
    },
    {
        icon: <LockOpenIcon sx={{ fontSize: 28, color: SECONDARY }} />,
        num: '02',
        label: 'Activation de votre compte Avance Immédiate de Crédit d\'Impôt',
        detail: "Votre Avance Immédiate est activée. Vous devez valider votre compte avec l'Avance Immédiate de Crédit d'Impôt.",
        time: 'Immédiat',
        color: SECONDARY,
    },
    {
        icon: <TrendingUpIcon sx={{ fontSize: 28, color: '#059669' }} />,
        num: '03',
        label: 'Réglez vos prestations en toute simplicité',
        detail: "Pour chaque prestation déclarée par votre prof, vous réglez directement depuis le compte Avance Immédiate, sans même avoir besoin de valider.",
        time: 'Continu',
        color: '#059669',
    },
];

const BENEFITS = [
    {
        icon: <EuroIcon sx={{ fontSize: 28, color: PRIMARY }} />,
        bg: `${PRIMARY}12`,
        border: `${PRIMARY}25`,
        title: 'Crédit d\'impôt immédiat',
        desc: 'Jusqu\'à 50 % déduits directement sur chaque facture sans attendre votre déclaration annuelle.',
        color: PRIMARY,
    },
    {
        icon: <AccountBalanceIcon sx={{ fontSize: 28, color: SECONDARY }} />,
        bg: '#4f46e508',
        border: '#4f46e520',
        title: 'Facturation 100 % gérée',
        desc: 'Verified Trainer SAP s\'occupe de toutes les démarches URSSAF. Zéro paperasse de votre côté.',
        color: SECONDARY,
    },
    {
        icon: <ShieldIcon sx={{ fontSize: 28, color: '#059669' }} />,
        bg: '#05966908',
        border: '#05966920',
        title: 'Service agréé & sécurisé',
        desc: "Teach'r et ses professeurs sont agréés Service à la Personne. Vos données sont protégées, chaque cours est déclaré.",
        color: '#059669',
    },
];

interface Props {
    profFirstName?: string;
    profLastName?: string;
    onStart: () => void;
}

const OnboardingLandingPage: React.FC<Props> = ({ profFirstName, profLastName, onStart }) => {
    const { brandConfig } = useTailwindBrand();
    const font = brandConfig.fontFamily;
    const { displayLogo } = useAppBar({ index: 1 });
    // const profName = profFirstName && profLastName ? fullNameChild(profFirstName, profLastName) : null;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'white', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <div className="bg-bgAiciBoarding bg-cover">
                <Box sx={{ px: { xs: 3, sm: 5, md: 10, lg: 14 }, py: { xs: 2, md: 4 } }}>
                    {displayLogo()}
                </Box>
                <Box sx={{ px: { xs: 3, sm: 5, md: 10, lg: 14 }, py: { xs: 6, md: 8 } }} className=" grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    {/* ── Gauche : texte ── */}
                    <Box>
                        <Typography sx={{
                            fontFamily: font, fontWeight: 900, color: brandConfig.colors.text,
                            fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem' },
                            lineHeight: 1.2, letterSpacing: '-1px', mb: 2.5,
                        }}>
                            Complétez{' '}
                            <Box component="span" sx={{
                                background: `linear-gradient(135deg, ${PRIMARY}, ${SECONDARY})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}>
                                votre formulaire
                            </Box>
                        </Typography>

                        <Typography sx={{
                            fontFamily: font, color: brandConfig.colors.text,
                            fontSize: { xs: '1rem', md: '1.15rem' },
                            lineHeight: 1.8, mb: 4, maxWidth: 520,
                        }}>
                            Grâce à l'<strong>Avance Immédiate de Crédit d'Impôt</strong>, le crédit d'impôt de 50%
                            est déduit directement à chaque cours
                            {profFirstName && <> avec <strong style={{ textTransform: 'capitalize' }}>{profFirstName}</strong></>}.
                            En 2 minutes, c'est activé. Gratuit, sécurisé.
                        </Typography>
                    </Box>

                    <div >
                        {/* ── Droite : panneau visuel ── */}
                        <Box sx={{
                            bgcolor: 'white', borderRadius: 4,
                            p: { xs: 3, md: 4 },
                            border: '1.5px solid #e8eef6',
                            boxShadow: `0 16px 48px ${PRIMARY}12`,
                        }}>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 2,
                                    p: 2, borderRadius: 3, bgcolor: '#f8faff', border: '1px solid #e8eef6',
                                }}>
                                    <img src={sapLogo} alt="Service à la Personne agréé" style={{ height: 40, objectFit: 'contain', flexShrink: 0 }} />
                                    <Box>
                                        <Typography sx={{ fontFamily: font, fontWeight: 700, color: '#0a1628', fontSize: '0.85rem' }}>
                                            Agréé Service à la Personne
                                        </Typography>
                                        <Typography sx={{ fontFamily: font, color: '#64748b', fontSize: '0.78rem' }}>
                                            Dispositif officiel de l'État
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 2,
                                    p: 2, borderRadius: 3, bgcolor: '#f8faff', border: '1px solid #e8eef6',
                                }}>
                                    <img
                                        src="/brands/verified-trainer/logo.svg"
                                        alt="Verified Trainer SAP"
                                        style={{ height: 28, objectFit: 'contain', flexShrink: 0 }}
                                        onError={(e) => { (e.target as HTMLImageElement).src = '/brands/verified-trainer/logo.png'; }}
                                    />
                                    <Box>
                                        <Typography sx={{ fontFamily: font, fontWeight: 700, color: '#0a1628', fontSize: '0.85rem' }}>
                                            Verified Trainer SAP, partenaire de Teach'r,
                                        </Typography>
                                        <Typography sx={{ fontFamily: font, color: '#64748b', fontSize: '0.78rem', lineHeight: 1.4 }}>
                                            assure pour vous toutes les démarches d'inscription et la gestion de la facturation auprès de l'URSSAF dans le cadre de vos cours particuliers.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {["100 % en ligne", "Formulaire en 2 minutes", "Aucune pièce d'identité requise"].map((txt, i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleIcon sx={{ color: '#059669', fontSize: 18 }} />
                                        <Typography sx={{ fontFamily: font, color: '#374151', fontSize: '0.88rem', fontWeight: 500 }}>
                                            {txt}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </div>
                </Box>
            </div>

            {/* ── PARCOURS D'ACTIVATION ── */}
            <Box sx={{ bgcolor: 'white', px: { xs: 3, sm: 5, md: 10, lg: 18 }, py: { xs: 6, md: 8 } }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                    <Typography sx={{ fontFamily: font, fontWeight: 900, color: brandConfig.colors.primary, fontSize: { xs: '1.5rem', md: '2rem' }, letterSpacing: '-0.5px', mb: 1 }}>
                        Votre parcours d'activation
                    </Typography>
                    <Typography sx={{ fontFamily: font, color: '#64748b', fontSize: '1rem', maxWidth: 480, mx: 'auto' }}>
                        3 étapes simples pour bénéficier de l'avance immédiate de crédit d'impôt sur chacun de vos {getActivePrestationBrandConfig("s")}.
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: { xs: 2, md: 3 } }} >
                    {STEPS.map((s, i) => (
                        <Box key={i} sx={{
                            bgcolor: 'white', borderRadius: 4, p: { xs: 3, md: 3.5 },
                            border: '1.5px solid #e8eef6',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            position: 'relative',
                            transition: 'transform .2s, box-shadow .2s',
                            '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 32px ${s.color}18` },
                        }}>
                            <Typography sx={{
                                fontFamily: font, fontWeight: 900,
                                fontSize: '2.8rem', lineHeight: 1,
                                color: `${s.color}`,
                                position: 'absolute', top: 12, right: 16,
                                letterSpacing: '-2px',
                            }}>
                                {s.num}
                            </Typography>

                            <Box sx={{
                                width: 52, height: 52, borderRadius: 3,
                                // bgcolor: s.bg,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                mb: 2,
                            }}>
                                {s.icon}
                            </Box>

                            <Chip
                                label={s.time}
                                size="small"
                                sx={{
                                    bgcolor: `${s.color}10`, color: s.color,
                                    fontWeight: 700, fontFamily: font, fontSize: '0.68rem',
                                    border: `1px solid ${s.color}25`, mb: 1.5, height: 20,
                                }}
                            />

                            <Typography sx={{ fontFamily: font, fontWeight: 800, color: s.color, fontSize: '0.95rem', mb: 0.75, lineHeight: 1.3 }}>
                                {s.label}
                            </Typography>
                            <Typography sx={{ fontFamily: font, color: brandConfig.colors.text, fontSize: '0.82rem', lineHeight: 1.6, mt: 1.5 }}>
                                {s.detail}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ── CE QUE VOUS GAGNEZ ── */}
            <Box sx={{ bgcolor: brandConfig.colors.background, px: { xs: 3, sm: 5, md: 10, lg: 18 }, py: { xs: 6, md: 8 } }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                    <Typography sx={{ fontFamily: font, fontWeight: 900, color: brandConfig.colors.primary, fontSize: { xs: '1.5rem', md: '2rem' }, letterSpacing: '-0.5px', mb: 1 }}>
                        Ce que vous gagnez
                    </Typography>
                    <Typography sx={{ fontFamily: font, color: brandConfig.colors.text, fontSize: '1rem', maxWidth: 480, mx: 'auto' }}>
                        L'avance immédiante de crédit d'impôt c'est un dispositif de l'État vous y avez droit, autant en profiter dès maintenant.
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
                    {BENEFITS.map((b, i) => (
                        <Box key={i} sx={{
                            borderRadius: 4, p: 3.5,
                            bgcolor: 'white',
                            border: `1.5px solid ${b.border}`,
                            transition: 'transform .2s',
                            '&:hover': { transform: 'translateY(-3px)' },
                        }}>
                            <Box sx={{ mb: 2 }}>{b.icon}</Box>
                            <Typography sx={{ fontFamily: font, fontWeight: 800, color: b.color, fontSize: '1rem', mb: 0.75 }}>
                                {b.title}
                            </Typography>
                            <Typography sx={{ fontFamily: font, color: brandConfig.colors.text, fontSize: '0.88rem', lineHeight: 1.7, mt: 1.5 }}>
                                {b.desc}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Chiffres clés */}
                <Box sx={{
                    mt: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3,
                    background: `white`,
                    borderRadius: 4, p: { xs: 3, md: 4 },
                    border: `1.5px solid ${PRIMARY}15`,
                }}>
                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <Typography sx={{ fontFamily: font, fontWeight: 900, color: PRIMARY, fontSize: { xs: '2.8rem', md: '3.5rem' }, lineHeight: 1, mb: 0.5 }}>
                            50 %
                        </Typography>
                        <Typography sx={{ fontFamily: font, fontWeight: 700, color: brandConfig.colors.primary, fontSize: '1rem', mb: 0.4 }}>
                            de crédit d'impôt par séance
                        </Typography>
                        <Typography sx={{ fontFamily: font, color: brandConfig.colors.text, fontSize: '0.85rem' }}>
                            Déduit directement à chaque cours pas besoin d'attendre.
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <Typography sx={{ fontFamily: font, fontWeight: 900, color: '#059669', fontSize: { xs: '2.8rem', md: '3.5rem' }, lineHeight: 1, mb: 0.5 }}>
                            6 000 €
                        </Typography>
                        <Typography sx={{ fontFamily: font, fontWeight: 700, color: brandConfig.colors.primary, fontSize: '1rem', mb: 0.4 }}>
                            de crédit d'impôt max / an
                        </Typography>
                        <Typography sx={{ fontFamily: font, color: brandConfig.colors.text, fontSize: '0.85rem' }}>
                            Pour un plafond annuel de 12 000 € de dépenses éligibles.
                        </Typography>
                    </Box>
                </Box>
            </Box>


            {/* ── CTA FINAL ── */}
            <Box sx={{
                px: { xs: 3, sm: 5, md: 10, lg: 18 },
                pt: { xs: 2, md: 3 }, pb: { xs: 6, md: 10 },
            }}>
                <Box sx={{
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${SECONDARY} 100%)`,
                    borderRadius: 5,
                    p: { xs: 4, md: 6 },
                    textAlign: 'center',
                    boxShadow: `0 16px 48px ${PRIMARY}35`,
                }}>
                    <Typography sx={{ fontFamily: font, fontWeight: 900, color: 'white', fontSize: { xs: '1.6rem', md: '2.2rem' }, letterSpacing: '-0.5px', mb: 1.5 }}>
                        Prêt·e à vous inscrire ?
                    </Typography>
                    <Typography sx={{ fontFamily: font, color: 'rgba(255,255,255,0.85)', fontSize: '1rem', mb: 4, maxWidth: 440, mx: 'auto', lineHeight: 1.7 }}>
                        Le formulaire prend <strong style={{ color: 'white' }}>2 minutes</strong>.
                        Munissez-vous de votre IBAN.
                    </Typography>

                    <ButtonField
                        label="Commencer le formulaire"
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        onClick={onStart}
                        style={{
                            fontSize: '1.05rem', padding: '15px 40px',
                            borderRadius: 12,
                            backgroundColor: 'white',
                            color: PRIMARY,
                            fontWeight: 800,
                            boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
                        }}
                    />

                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                        {['100 % gratuit', 'Données sécurisées', 'Agréé État'].map((txt, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <CheckCircleIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 15 }} />
                                <Typography sx={{ fontFamily: font, color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem', fontWeight: 600 }}>
                                    {txt}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default OnboardingLandingPage;
